// src/utils/fechasUtils.js
import feriadoService from '../services/feriadoService';

let feriadosCache = null;
let ultimaActualizacion = null;

/**
 * Obtiene los feriados activos y los mantiene en caché por 5 minutos
 */
const obtenerFeriadosActivos = async () => {
    const ahora = Date.now();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

    if (feriadosCache && ultimaActualizacion && (ahora - ultimaActualizacion < CACHE_DURATION)) {
        return feriadosCache;
    }

    try {
        const response = await feriadoService.obtenerActivos();

        // Asegurar que devuelve un array válido
        if (response && Array.isArray(response.data)) {
             feriadosCache = response.data;
        } else if (Array.isArray(response)) {
             // Por si el interceptor ya devolvió los datos
             feriadosCache = response;
        } else {
             console.warn("Respuesta de feriados inválida:", response);
             feriadosCache = [];
        }

        ultimaActualizacion = ahora;
        return feriadosCache;
    } catch (error) {
        console.error('Error al obtener feriados:', error);
        return feriadosCache || []; // Retornar caché viejo si existe, o array vacío
    }
};

/**
 * Formatea una fecha a string YYYY-MM-DD usando la zona horaria local
 * Exportada para uso en otros componentes
 */
export const formatDateISO = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Formatea una fecha a string largo en español "dddd, D de MMMM de YYYY"
 * usando la zona horaria local (sin conversión UTC)
 */
export const formatDateLong = (date) => {
    if (!date) return '';
    const meses = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    const diasSemana = [
        'domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'
    ];

    const year = date.getFullYear();
    const month = meses[date.getMonth()];
    const day = date.getDate();
    const weekday = diasSemana[date.getDay()];

    return `${weekday}, ${day} de ${month} de ${year}`;
};

/**
 * Verifica si una fecha es feriado
 */
const esFeriado = (fecha, feriados) => {
    if (!feriados || !Array.isArray(feriados)) return false;
    const fechaStr = formatDateISO(fecha);
    return feriados.some(feriado => feriado && feriado.fecha === fechaStr);
};

/**
 * Verifica si una fecha es fin de semana (sábado o domingo)
 */
const esFinDeSemana = (fecha) => {
    const dia = fecha.getDay();
    return dia === 0 || dia === 6; // 0 = domingo, 6 = sábado
};

/**
 * Verifica si una fecha es día hábil (no es fin de semana ni feriado)
 */
const esDiaHabil = (fecha, feriados) => {
    return !esFinDeSemana(fecha) && !esFeriado(fecha, feriados);
};

/**
 * Calcula días hábiles desde una fecha considerando fines de semana y feriados
 * @param {Date} fechaInicio - Fecha de inicio
 * @param {number} dias - Número de días hábiles a agregar
 * @param {Array} feriados - Lista de feriados (opcional, si no se pasa se consultan)
 * @returns {Promise<Date>} - Fecha resultante después de agregar los días hábiles
 */
export const calcularDiasHabiles = async (fechaInicio, dias, feriados = null) => {
    if (!feriados) {
        feriados = await obtenerFeriadosActivos();
    }

    let fecha = new Date(fechaInicio);
    // Asegurarse de no mutar la fecha original y de trabajar con horas en 0
    // Pero si fechaInicio ya tiene hora seteada, mejor clonar

    let diasAgregados = 0;

    // Comenzamos a sumar desde el día siguiente
    // La lógica de "5 días hábiles a partir de mañana" implica empezar el loop en +1
    // Pero el loop actual hace fecha.setDate(fecha.getDate() + 1) al principio,
    // así que efectivamente empieza evaluando el día siguiente. Es correcto.

    while (diasAgregados < dias) {
        fecha.setDate(fecha.getDate() + 1);
        
        if (esDiaHabil(fecha, feriados)) {
            diasAgregados++;
        }
    }

    return fecha;
};

/**
 * Calcula días hábiles de forma síncrona (requiere que se pasen los feriados)
 * @param {Date} fechaInicio - Fecha de inicio
 * @param {number} dias - Número de días hábiles a agregar
 * @param {Array} feriados - Lista de feriados
 * @returns {Date} - Fecha resultante después de agregar los días hábiles
 */
export const calcularDiasHabilesSync = (fechaInicio, dias, feriados = []) => {
    let fecha = new Date(fechaInicio);
    let diasAgregados = 0;

    while (diasAgregados < dias) {
        fecha.setDate(fecha.getDate() + 1);
        
        if (esDiaHabil(fecha, feriados)) {
            diasAgregados++;
        }
    }

    return fecha;
};

/**
 * Cuenta los días hábiles entre dos fechas
 * @param {Date} fechaInicio - Fecha de inicio
 * @param {Date} fechaFin - Fecha de fin
 * @param {Array} feriados - Lista de feriados (opcional, si no se pasa se consultan)
 * @returns {Promise<number>} - Número de días hábiles entre las dos fechas
 */
export const contarDiasHabiles = async (fechaInicio, fechaFin, feriados = null) => {
    if (!feriados) {
        feriados = await obtenerFeriadosActivos();
    }

    // Normalizar fechas a inicio del día para comparación correcta
    let fecha = new Date(fechaInicio);
    fecha.setHours(0, 0, 0, 0);

    const fin = new Date(fechaFin);
    fin.setHours(0, 0, 0, 0);

    let diasHabiles = 0;

    // Si la fecha de inicio es posterior al fin, retorna 0 (o negativo si se quiere)
    if (fecha > fin) return 0;

    // Avanzamos día por día desde el siguiente día del inicio hasta llegar al fin?
    // "días restantes" suele incluir el día final pero no el inicial si ya pasó...
    // O suele ser la cantidad de días hábiles en el intervalo [inicio, fin].
    // Si hoy es 10 y fin es 11.
    // Si hoy es martes(habil) y mañana miercoles(habil). Días restantes para el vencimiento.
    // Usualmente se cuenta desde mañana hasta el vencimiento inclusive?

    // Asumiremos intervalo [fecha, fin] inclusivo, salto fecha inicio si ya pasó
    // Pero como fechaInicio va avanzando en el while loop

    // Corrección para "Días Restantes":
    // Si Hoy es 10. Vence el 19.
    // Días hábiles entre 10 y 19.

    // Si queremos "días restantes", no deberíamos contar el día actual si ya "pasó" o si se considera que el plazo corre al día siguiente.
    // Pero si estoy "dentro" del plazo, quiero saber cuántos días de trabajo tengo.

    // Voy a mantener la lógica simple: contar cuántos días en el rango son hábiles.

    while (fecha <= fin) {
        // La implementación original incluía fecha inicio.
         if (esDiaHabil(fecha, feriados)) {
            diasHabiles++;
        }
        fecha.setDate(fecha.getDate() + 1);
    }

    // Nota: Si se quiere contar "días restantes" desde "hoy" (excluyendo hoy), mejor pasar hoy+1.
    // Pero la función es genérica "contarDiasHabiles".

    return diasHabiles;
};

/**
 * Cuenta los días hábiles entre dos fechas (Síncrono)
 * Requiere pasar feriados
 */
export const contarDiasHabilesSync = (fechaInicio, fechaFin, feriados = []) => {
    let fecha = new Date(fechaInicio);
    fecha.setHours(0, 0, 0, 0);

    const fin = new Date(fechaFin);
    fin.setHours(0, 0, 0, 0);

    let diasHabiles = 0;

    if (fecha > fin) return 0;

    while (fecha <= fin) {
        if (esDiaHabil(fecha, feriados)) {
            diasHabiles++;
        }
        fecha.setDate(fecha.getDate() + 1);
    }

    return diasHabiles;
};

/**
 * Limpia la caché de feriados (útil cuando se agregan o eliminan feriados)
 */
export const limpiarCacheFeriados = () => {
    feriadosCache = null;
    ultimaActualizacion = null;
};

/**
 * Obtiene información de un feriado para una fecha específica
 */
export const obtenerInfoFeriado = async (fecha) => {
    const feriados = await obtenerFeriadosActivos();
    const fechaStr = formatDateISO(new Date(fecha));
    return feriados.find(feriado => feriado.fecha === fechaStr);
};

export default {
    calcularDiasHabiles,
    calcularDiasHabilesSync,
    contarDiasHabiles,
    contarDiasHabilesSync,
    obtenerFeriadosActivos,
    limpiarCacheFeriados,
    obtenerInfoFeriado,
    esFeriado,
    esFinDeSemana,
    esDiaHabil,
    formatDateISO,
    formatDateLong
};



